import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import { SAUDI_DEDUCTION_TYPE_VALUES, getGosiDeclRates } from './utils/payrollSaudiFormat';
import { useSalairesConfig } from './useSalairesConfig';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  AlertTriangle, Search, Plus, Edit, Trash2, RefreshCcw, CheckCircle, AlertCircle, XCircle,
  Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X,
  LayoutGrid, Layers, TrendingUp, DollarSign, FileText, Users, Percent,
  List, LayoutList, Shield, Lock, Clock, Pause, Play
} from 'lucide-react';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import localApiAdapter from '../../core/services/localApiAdapter';
import { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import { useQawaemTableColumnVisibility, withQawaemActionsColumn, countVisibleTableCols } from '../../shared/hooks/useQawaemTableColumnVisibility';
import { ensureStatusBeforeActions } from '../../shared/utils/erpListColumnOrder';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import {
  SalairesListControlsStrip,
  useClearSearchOnTableView,
  renderSalairesBulkDeleteFilterAction,
  useSalairesTableSelection,
  SALAIRES_FORM_GRID_CLASS,
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  QawaemTableStickyActionsTd,
  QAWAEM_TABLE_ACTIONS_TH_CLASS,
  useSalairesEmployeeOptions,
} from './salairesListPageUtils';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import CustomDatePicker from '../../shared/components/custom/CustomDatePicker';
import '../../shared/styles/GPSTrackingDatePicker.css';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { QawaemErpModalFormTabBar } from '../../products/saudi/components/QawaemErpModalFormTabs';

const NS = 'modules.salaires.pages.retenuesDiversesPage';

const EMPTY_RETENUE_FORM = {
  code: '',
  employee_id: '',
  employee_name: '',
  employee_code: '',
  type_retenue: '',
  categorie: '',
  montant: '',
  montant_fixe: 1,
  pourcentage: '',
  base_calcul: 'salaire_net',
  date_debut: '',
  date_fin: '',
  frequence: 'mensuel',
  nombre_echeances: '',
  echeances_restantes: '',
  statut: 'actif',
  priorite: '1',
  beneficiaire: '',
  reference_externe: '',
  organisme: '',
  notes: '',
  document_justificatif: '',
};

const RetenuesDiversesPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`${NS}.${key}`, opts);
  const dateLocale = getBrandLocale(i18n.language);
  const { isDashboardLightMode } = useGlobalTheme();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_deduction', { defaultValue: 'استقطاع' });
  const { employees } = useSalairesEmployeeOptions();
  const salariesConfig = useSalairesConfig();
  const gosiEmployeeRate = useMemo(
    () => getGosiDeclRates(salariesConfig).gosiEmployeeRate,
    [salariesConfig],
  );
  const [saving, setSaving] = useState(false);

  const formatAmountDisplay = (val) => {
    if (val == null || val === '') return p('not_available');
    const n = parseFloat(val);
    if (Number.isNaN(n)) return p('not_available');
    return formatBrandMoney(n);
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [retenues, setRetenues] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  useClearSearchOnTableView(viewMode, searchTerm, setSearchTerm);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [formTab, setFormTab] = useState('general');

  const retenueFormTabs = useMemo(() => ([
    { id: 'general', label: p('section_deduction'), icon: AlertTriangle, iconClassName: 'text-blue-400' },
    { id: 'calculation', label: p('section_calculation'), icon: Percent, iconClassName: 'text-green-400' },
    { id: 'additional', label: p('section_additional'), icon: FileText, iconClassName: 'text-purple-400' },
  ]), [p]);
  
  const [formData, setFormData] = useState({ ...EMPTY_RETENUE_FORM });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_retenues');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  const {
    filters: columnFilters,
    setFilter: setColumnFilter,
    editingKey: columnEditingKey,
    setEditingKey: setColumnEditingKey,
  } = useColumnHeaderFilters();

  useEffect(() => { loadRetenues(); }, []);

  useEffect(() => {
    if (!showModal && !showViewModal) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [showModal, showViewModal]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadRetenues = async () => {
    try {
      const response = await localApiAdapter.get('/salaires/retenues-diverses');
      setRetenues(sortRecordsNewestFirst(response?.data || response || []));
    } catch (error) {
      console.error('Error loading retenues:', error);
      setRetenues([]);
    }
  };

  const stats = {
    total: retenues.length,
    actif: retenues.filter(r => r.statut === 'actif').length,
    suspendu: retenues.filter(r => r.statut === 'suspendu').length,
    termine: retenues.filter(r => r.statut === 'termine').length,
    totalMontant: formatBrandMoney(retenues.reduce((sum, r) => sum + (parseFloat(r.montant) || 0), 0))
  };

  const mainTabs = useMemo(() => [
    { id: 'all_retenues', name: t('common.general_view'), icon: LayoutGrid },
    { id: 'par_statut', name: t('common.by_status'), icon: Shield },
    { id: 'par_type', name: t('common.by_type'), icon: AlertTriangle },
    { id: 'par_categorie', name: p('tab_by_category'), icon: Layers }
  ], [t, p]);

  const quickFilterButtons = useMemo(() => [
    { id: 'all', name: t('common.all'), icon: Layers },
    { id: 'actif', name: p('filter_active'), icon: Play },
    { id: 'suspendu', name: p('filter_suspended'), icon: Pause },
    { id: 'termine', name: p('filter_finished'), icon: CheckCircle }
  ], [t, p]);

  const getTypeDistribution = useMemo(() => {
    const gosi = retenues.filter(r => r.type_retenue === SAUDI_DEDUCTION_TYPE_VALUES.gosi).length;
    const loan = retenues.filter(r => r.type_retenue === SAUDI_DEDUCTION_TYPE_VALUES.loanRepayment).length;
    const absence = retenues.filter(r => r.type_retenue === SAUDI_DEDUCTION_TYPE_VALUES.absence).length;
    const lateness = retenues.filter(r => r.type_retenue === SAUDI_DEDUCTION_TYPE_VALUES.lateness).length;
    const penalty = retenues.filter(r => r.type_retenue === SAUDI_DEDUCTION_TYPE_VALUES.penalty).length;
    const autre = retenues.filter(r => r.type_retenue === SAUDI_DEDUCTION_TYPE_VALUES.other).length;
    return [
      { filterKey: SAUDI_DEDUCTION_TYPE_VALUES.gosi, name: p('type_gosi', { rate: gosiEmployeeRate }), count: gosi, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
      { filterKey: SAUDI_DEDUCTION_TYPE_VALUES.loanRepayment, name: p('type_loan_repayment'), count: loan, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
      { filterKey: SAUDI_DEDUCTION_TYPE_VALUES.absence, name: p('type_absence'), count: absence, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
      { filterKey: SAUDI_DEDUCTION_TYPE_VALUES.lateness, name: p('type_lateness'), count: lateness, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
      { filterKey: SAUDI_DEDUCTION_TYPE_VALUES.penalty, name: p('type_penalty'), count: penalty, color: 'bg-gradient-to-r from-red-500 to-red-600' },
      { filterKey: SAUDI_DEDUCTION_TYPE_VALUES.other, name: p('type_other'), count: autre, color: 'bg-gradient-to-r from-gray-500 to-gray-600' },
    ];
  }, [retenues, p, gosiEmployeeRate]);

  const getCategorieDistribution = useMemo(() => {
    const legale = retenues.filter(r => r.categorie === 'legale' || r.categorie === 'legal').length;
    const conventionnelle = retenues.filter(r => r.categorie === 'conventionnelle' || r.categorie === 'contractual').length;
    const disciplinaire = retenues.filter(r => r.categorie === 'disciplinaire' || r.categorie === 'disciplinary').length;
    const volontaire = retenues.filter(r => r.categorie === 'volontaire' || r.categorie === 'voluntary').length;
    return [
      { filterKey: 'legale', name: p('category_legal'), count: legale, color: 'bg-gradient-to-r from-red-500 to-red-600' },
      { filterKey: 'conventionnelle', name: p('category_contractual'), count: conventionnelle, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
      { filterKey: 'disciplinaire', name: p('category_disciplinary'), count: disciplinaire, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
      { filterKey: 'volontaire', name: p('category_voluntary'), count: volontaire, color: 'bg-gradient-to-r from-green-500 to-green-600' },
    ];
  }, [retenues, p]);

  const retenueDistribution = useMemo(() => ({
    all_retenues: {
      title: p('dist_overview_title'),
      data: [
        { filterKey: 'actif', name: p('dist_active'), count: stats.actif, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'suspendu', name: p('dist_suspended'), count: stats.suspendu, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { filterKey: 'termine', name: p('dist_finished'), count: stats.termine, color: 'bg-gradient-to-r from-gray-500 to-gray-600' },
        { filterKey: 'annule', name: p('dist_cancelled'), count: retenues.filter(r => r.statut === 'annule').length, color: 'bg-gradient-to-r from-red-500 to-red-600' }
      ]
    },
    par_statut: {
      title: p('dist_by_status_title'),
      data: [
        { filterKey: 'actif', name: p('dist_active'), count: stats.actif, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'suspendu', name: p('dist_suspended'), count: stats.suspendu, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { filterKey: 'termine', name: p('dist_finished'), count: stats.termine, color: 'bg-gradient-to-r from-gray-500 to-gray-600' },
        { filterKey: 'annule', name: p('dist_cancelled'), count: retenues.filter(r => r.statut === 'annule').length, color: 'bg-gradient-to-r from-red-500 to-red-600' }
      ]
    },
    par_type: {
      title: p('dist_by_type_title'),
      data: getTypeDistribution
    },
    par_categorie: {
      title: p('dist_by_category_title'),
      data: getCategorieDistribution
    }
  }), [retenues, stats, p, getTypeDistribution, getCategorieDistribution]);

  const distributionInfo = retenueDistribution[mainViewMode] || retenueDistribution.all_retenues;

  const columnFilterRules = useMemo(
    () => [textColumnRule('code', (row) => row.code || '')],
    [],
  );

  const colHeaderFilterProps = (key, label, extra = {}) => ({
    columnKey: key,
    label,
    value: columnFilters[key] ?? '',
    onChange: (v) => setColumnFilter(key, v),
    editingKey: columnEditingKey,
    onEditingKeyChange: setColumnEditingKey,
    thClassName: 'text-white font-semibold',
    ...extra,
  });

  const filteredRetenues = useMemo(() => {
    const base = retenues.filter(r => {
    if (searchTerm && !r.code?.toLowerCase().includes(searchTerm.toLowerCase()) && !r.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) && !r.type_retenue?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (mainViewMode === 'all_retenues') {
      if (quickFilter === 'actif' && r.statut !== 'actif') return false;
      if (quickFilter === 'suspendu' && r.statut !== 'suspendu') return false;
      if (quickFilter === 'termine' && r.statut !== 'termine') return false;
    }
    if (activeDistributionFilter) {
      switch (mainViewMode) {
        case 'all_retenues':
        case 'par_statut':
          if (activeDistributionFilter !== r.statut) return false;
          break;
        case 'par_type':
          if (activeDistributionFilter !== r.type_retenue) return false;
          break;
        case 'par_categorie':
          if (activeDistributionFilter !== r.categorie) return false;
          break;
      }
    }
    return true;
  });

    const filtered = applyColumnFilters(base, columnFilters, columnFilterRules);
    return sortRecordsNewestFirst(filtered);
  }, [retenues, searchTerm, mainViewMode, quickFilter, activeDistributionFilter, columnFilters, columnFilterRules]);

  const {
    selectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
    clearSelection,
  } = useSalairesTableSelection(filteredRetenues);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const submitData = { ...formData };
      if (editingRecord) {
        await localApiAdapter.put(`/salaires/retenues-diverses/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/salaires/retenues-diverses', submitData);
        notifyCreated(showNotify, entityLabel);
      }
      resetForm();
      loadRetenues();
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('save_error'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormTab('general');
    setFormData({ ...record });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_deduction_confirm'))) {
      try {
        await localApiAdapter.delete(`/salaires/retenues-diverses/${id}`);
        notifyDeleted(showNotify, entityLabel);
        loadRetenues();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('save_error'));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('delete_deductions_bulk', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/salaires/retenues-diverses/${id}`)));
        clearSelection();
        notifyBulkDeleted(showNotify, deleteCount, entityLabel);
        loadRetenues();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('save_error'));
      }
    }
  };

  const resetForm = () => {
    setFormData({ ...EMPTY_RETENUE_FORM });
    setEditingRecord(null);
    setFormTab('general');
    setShowModal(false);
  };

  const statusOptions = useMemo(() => [
    { value: 'actif', label: p('status_active'), icon: Play, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'suspendu', label: p('status_suspended'), icon: Pause, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { value: 'termine', label: p('status_finished'), icon: CheckCircle, style: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
    { value: 'annule', label: p('status_cancelled'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' }
  ], [p]);

  const typeRetenueOptions = useMemo(() => [
    { value: SAUDI_DEDUCTION_TYPE_VALUES.gosi, label: p('type_gosi', { rate: gosiEmployeeRate }) },
    { value: SAUDI_DEDUCTION_TYPE_VALUES.loanRepayment, label: p('type_loan_repayment') },
    { value: SAUDI_DEDUCTION_TYPE_VALUES.absence, label: p('type_absence') },
    { value: SAUDI_DEDUCTION_TYPE_VALUES.lateness, label: p('type_lateness') },
    { value: SAUDI_DEDUCTION_TYPE_VALUES.penalty, label: p('type_penalty') },
    { value: SAUDI_DEDUCTION_TYPE_VALUES.other, label: p('type_other') },
  ], [p, gosiEmployeeRate]);

  const categorieOptions = useMemo(() => [
    { value: 'legale', label: p('category_legal') },
    { value: 'conventionnelle', label: p('category_contractual') },
    { value: 'disciplinaire', label: p('category_disciplinary') },
    { value: 'volontaire', label: p('category_voluntary') },
  ], [p]);

  const frequenceOptions = useMemo(() => [
    { value: 'mensuel', label: p('freq_monthly') },
    { value: 'trimestriel', label: p('freq_quarterly') },
    { value: 'annuel', label: p('freq_annual') },
    { value: 'unique', label: p('freq_once') },
  ], [p]);

  const baseCalculOptions = useMemo(() => [
    { value: 'salaire_brut', label: p('base_gross') },
    { value: 'salaire_net', label: p('base_net') },
    { value: 'salaire_base', label: p('base_basic') },
  ], [p]);

  const employeeSelectOptions = useMemo(
    () => employees.map((emp) => ({
      value: String(emp.id),
      label: `${emp.prenom || ''} ${emp.nom || ''}`.trim() + (emp.mat ? ` (${emp.mat})` : ''),
    })),
    [employees],
  );

  const getTypeLabel = (value) => typeRetenueOptions.find((o) => o.value === value)?.label || value || p('not_available');
  const getCategoryLabel = (value) => categorieOptions.find((o) => o.value === value)?.label || value || p('not_available');

  const openCreateModal = () => {
    setFormData({ ...EMPTY_RETENUE_FORM });
    setEditingRecord(null);
    setFormTab('general');
    setShowModal(true);
  };

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      const record = retenues.find((r) => r.id === recordId);
      if (!record) return;
      await localApiAdapter.put(`/salaires/retenues-diverses/${recordId}`, { ...record, statut: newStatus });
      await loadRetenues();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const qawaemBaseColumns = useMemo(() => ensureStatusBeforeActions([
    { key: 'code', label: p('col_code'), required: true },
    { key: 'employee', label: p('col_employee'), required: true },
    { key: 'type', label: p('col_type') },
    { key: 'priority', label: p('col_priority') },
    { key: 'amount', label: p('col_amount') },
    { key: 'status', label: t('common.status') },
  ]), [p, t]);
  const { handleToggleColumn, showCol, pickerColumns, visibleColumnCount } = useQawaemTableColumnVisibility(qawaemBaseColumns);
  const qawaemColumns = useMemo(
    () => withQawaemActionsColumn(pickerColumns, t('common.actions')),
    [pickerColumns, t],
  );
  const visibleTableColCount = useMemo(
    () => countVisibleTableCols(qawaemBaseColumns, showCol),
    [qawaemBaseColumns, showCol],
  );

  return (
    <div className={`space-y-3 employees-container`}>
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <AlertTriangle className="h-8 w-8 text-blue-400" />
            {p('title')}
            <button onClick={loadRetenues} className="refresh-button"><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
          >
            <Filter className="h-5 w-5" /><span className="hidden sm:inline">{t('common.advanced_options')}</span>
          </button>
          <button
            type="button"
            onClick={openCreateModal}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-xl hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
          >
            <Plus size={20} /><span>{p('new_deduction')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: p('stat_total'), value: stats.total, icon: List, color: 'from-blue-500 to-blue-600' },
          { title: p('stat_active'), value: stats.actif, icon: Play, color: 'from-green-500 to-green-600' },
          { title: p('stat_suspended'), value: stats.suspendu, icon: Pause, color: 'from-yellow-500 to-yellow-600' },
          { title: p('stat_finished'), value: stats.termine, icon: CheckCircle, color: 'from-purple-500 to-purple-600' },
          { title: p('stat_total_amount'), value: stats.totalMontant, icon: DollarSign, color: 'from-orange-500 to-orange-600' },
        ].slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div><p className="text-white/60 text-sm font-medium">{stat.title}</p><p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p></div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_retenues' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>{distributionInfo.data.map((item) => (
                <button key={item.filterKey || item.name} onClick={() => setActiveDistributionFilter(activeDistributionFilter === (item.filterKey || item.name) ? null : (item.filterKey || item.name))} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === (item.filterKey || item.name) ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white/80' : 'text-white/60'}`}>{p('pct_of_total', { pct: (() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })() })}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="glass-panel glass-table-shell overflow-visible">
        <SalairesListControlsStrip
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
          isDashboardLightMode={isDashboardLightMode}
          cardsTitle={t('common.view_cards', { defaultValue: 'بطاقات' })}
          tableTitle={t('common.view_table', { defaultValue: 'جدول' })}
          filterActions={renderSalairesBulkDeleteFilterAction({
            selectedCount: selectedRecords.length,
            onBulkDelete: handleBulkDelete,
            t,
          })}
        />
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                  />
                  {showCol('code') ? <TableColumnHeaderFilter {...colHeaderFilterProps('code', p('col_code'))} /> : null}
                  {showCol('employee') ? <th className="px-6 py-4 text-left text-white font-semibold">{p('col_employee')}</th> : null}
                  {showCol('type') ? <th className="px-6 py-4 text-left text-white font-semibold">{p('col_type')}</th> : null}
                  {showCol('priority') ? <th className="px-6 py-4 text-center text-white font-semibold">{p('col_priority')}</th> : null}
                  {showCol('amount') ? <th className="px-6 py-4 text-left text-white font-semibold">{p('col_amount')}</th> : null}
                  {showCol('status') ? <th className="px-6 py-4 text-center text-white font-semibold">{t('common.status')}</th> : null}
                  <QawaemTableHeaderActionsCell
                    label={t('common.actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={visibleColumnCount}
                    totalCount={qawaemColumns.length}
                    thClassName={QAWAEM_TABLE_ACTIONS_TH_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {filteredRetenues.length === 0 ? (
                  <tr><td colSpan={visibleTableColCount} className="text-center py-12 text-white/60">{p('empty')}</td></tr>
                ) : (
                  filteredRetenues.map((record) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                    <QawaemTableSelectRowCell
                      checked={selectedRecords.includes(record.id)}
                      onChange={() => handleSelectRecord(record.id)}
                    />
                    {showCol('code') ? (
                      <td className="px-6 py-4"><span className="text-white font-semibold">{record.code}</span></td>
                    ) : null}
                    {showCol('employee') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.employee_name}</span>
                          <span className="text-white/50 text-xs mt-0.5">{record.employee_code || p('not_available')}</span>
                        </div>
                      </td>
                    ) : null}
                    {showCol('type') ? (
                      <td className="px-6 py-4"><span className="text-white/70 text-sm">{record.type_retenue}</span></td>
                    ) : null}
                    {showCol('priority') ? (
                      <td className="px-6 py-4 text-center"><span className="text-white font-bold text-lg">{record.priorite || '-'}</span></td>
                    ) : null}
                    {showCol('amount') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-bold text-lg">{formatAmountDisplay(record.montant)}</span>
                          {record.montant_fixe === 0 && record.pourcentage && (
                            <span className="text-white/50 text-xs mt-0.5">{record.pourcentage}%</span>
                          )}
                        </div>
                      </td>
                    ) : null}
                    {showCol('status') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge
                            status={record.statut}
                            recordId={record.id}
                            options={statusOptions}
                            onChange={handleStatusChange}
                            title={p('change_status_title', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
                          />
                        </div>
                      </td>
                    ) : null}
                    <QawaemTableStickyActionsTd>
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                    </QawaemTableStickyActionsTd>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredRetenues.length === 0 ? (
              <div className="text-center py-12 text-white/60">{p('empty')}</div>
            ) : (
              filteredRetenues.map((record) => (
                <div key={record.id} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-3 mb-3">
                        <h3 className="text-xl font-bold text-white">{record.code}</h3>
                        <QawaemClickableStatusBadge
                          status={record.statut}
                          recordId={record.id}
                          options={statusOptions}
                          onChange={handleStatusChange}
                          title={p('change_status_title', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
                        />
                      </div>
                      <p className="text-white/70">{record.employee_name}</p>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {showViewModal && viewingRecord ? (
      <QawaemErpViewModal
        open={showViewModal}
        onClose={() => { setShowViewModal(false); setViewingRecord(null); }}
        title={p('view_title')}
        icon={Eye}
        testId="retenues-view-modal"
      >
          <div className="space-y-4">
            <div className="bg-white/5 rounded-xl p-5 border border-white/20">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Users size={18} className="text-blue-400" />
                {p('section_employee')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><p className="text-white/60 text-sm mb-1">{p('col_code')}</p><p className="text-white font-medium">{viewingRecord.code || p('not_available')}</p></div>
                <div><p className="text-white/60 text-sm mb-1">{p('col_employee')}</p><p className="text-white font-medium">{viewingRecord.employee_name || p('not_available')}</p></div>
                <div><p className="text-white/60 text-sm mb-1">{p('field_employee')}</p><p className="text-white font-medium">{viewingRecord.employee_code || p('not_available')}</p></div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <h3 className="text-white font-semibold mb-4">{p('section_deduction')}</h3>
                <div className="space-y-3">
                  <div><p className="text-white/60 text-sm mb-1">{p('col_type')}</p><p className="text-white font-medium">{getTypeLabel(viewingRecord.type_retenue)}</p></div>
                  <div><p className="text-white/60 text-sm mb-1">{p('field_category')}</p><p className="text-white font-medium">{getCategoryLabel(viewingRecord.categorie)}</p></div>
                  <div><p className="text-white/60 text-sm mb-1">{t('common.status')}</p><div className="mt-1"><QawaemClickableStatusBadge status={viewingRecord.statut} recordId={viewingRecord.id} options={statusOptions} onChange={handleStatusChange} /></div></div>
                  <div><p className="text-white/60 text-sm mb-1">{p('col_priority')}</p><p className="text-white font-medium">{viewingRecord.priorite ?? p('not_available')}</p></div>
                </div>
              </div>
              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <DollarSign size={18} className="text-emerald-400" />
                  {p('section_calculation')}
                </h3>
                <div className="space-y-3">
                  <div><p className="text-white/60 text-sm mb-1">{p('col_amount')}</p><p className="text-emerald-400 font-bold text-xl">{formatAmountDisplay(viewingRecord.montant)}</p></div>
                  {viewingRecord.montant_fixe === 0 && viewingRecord.pourcentage ? (
                    <div><p className="text-white/60 text-sm mb-1">{p('field_percentage')}</p><p className="text-white font-medium">{viewingRecord.pourcentage}%</p></div>
                  ) : null}
                  <div><p className="text-white/60 text-sm mb-1">{p('field_start_date')}</p><p className="text-white font-medium">{viewingRecord.date_debut ? new Date(viewingRecord.date_debut).toLocaleDateString(dateLocale) : p('not_available')}</p></div>
                  {viewingRecord.date_fin ? (
                    <div><p className="text-white/60 text-sm mb-1">{p('field_end_date')}</p><p className="text-white font-medium">{new Date(viewingRecord.date_fin).toLocaleDateString(dateLocale)}</p></div>
                  ) : null}
                </div>
              </div>
            </div>
            {(viewingRecord.beneficiaire || viewingRecord.organisme || viewingRecord.reference_externe || viewingRecord.notes) ? (
              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <h3 className="text-white font-semibold mb-4">{p('section_additional')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  {viewingRecord.beneficiaire ? <div><p className="text-white/60 mb-1">{p('field_beneficiary')}</p><p className="text-white">{viewingRecord.beneficiaire}</p></div> : null}
                  {viewingRecord.organisme ? <div><p className="text-white/60 mb-1">{p('field_organization')}</p><p className="text-white">{viewingRecord.organisme}</p></div> : null}
                  {viewingRecord.reference_externe ? <div><p className="text-white/60 mb-1">{p('field_external_ref')}</p><p className="text-white">{viewingRecord.reference_externe}</p></div> : null}
                  {viewingRecord.notes ? <div className="md:col-span-2"><p className="text-white/60 mb-1">{p('field_notes')}</p><p className="text-white/80">{viewingRecord.notes}</p></div> : null}
                </div>
              </div>
            ) : null}
          </div>
      </QawaemErpViewModal>
      ) : null}

      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className={`backdrop-blur-lg rounded-2xl max-h-[90vh] flex flex-col overflow-hidden ${isDashboardLightMode ? 'qawaem-modal-light border' : 'border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800'}`} style={isDashboardLightMode ? { boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)', width: '90%', maxWidth: '1200px' } : { boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }} onClick={(e) => e.stopPropagation()} data-testid="retenues-crud-modal">
            <div className={`flex items-center justify-between p-6 border-b shrink-0 ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold flex items-center gap-2 ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}><AlertTriangle className="h-6 w-6 text-blue-400" />{editingRecord ? p('modal_edit') : p('modal_new')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="retenues-form" disabled={saving} className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30 disabled:opacity-50"><CheckCircle size={18} />{t('common.save')}</button>
                <button type="button" onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}><X size={24} /></button>
              </div>
            </div>
            <QawaemErpModalFormTabBar tabs={retenueFormTabs} activeTab={formTab} onTabChange={setFormTab} isLight={isDashboardLightMode} ariaLabel={editingRecord ? p('modal_edit') : p('modal_new')} />
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="retenues-form" onSubmit={handleSubmit}>
                {formTab === 'general' && (
                  <div className={SALAIRES_FORM_GRID_CLASS}>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_code')}</label><input type="text" value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('col_code')} /></div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('field_employee')} required /></label><CustomSelect value={formData.employee_id ? String(formData.employee_id) : ''} onChange={(value) => { const emp = employees.find((e) => String(e.id) === String(value)); setFormData({ ...formData, employee_id: value, employee_name: emp ? `${emp.prenom || ''} ${emp.nom || ''}`.trim() : formData.employee_name, employee_code: emp?.mat || formData.employee_code }); }} options={employeeSelectOptions} placeholder={t('common.select')} autoSelect={false} searchable creatable /></div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('field_type')} required /></label><CustomSelect value={formData.type_retenue} onChange={(v) => setFormData({ ...formData, type_retenue: v })} options={typeRetenueOptions} placeholder={t('common.select')} autoSelect={false} searchable creatable /></div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_category')}</label><CustomSelect value={formData.categorie} onChange={(v) => setFormData({ ...formData, categorie: v })} options={categorieOptions} placeholder={t('common.select')} autoSelect={false} searchable creatable /></div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_status')}</label><CustomSelect value={formData.statut} onChange={(v) => setFormData({ ...formData, statut: v })} options={statusOptions.map((s) => ({ value: s.value, label: s.label }))} autoSelect={false} searchable creatable /></div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_priority')}</label><CustomNumberInput min={1} step={1} value={formData.priorite} onChange={(v) => setFormData({ ...formData, priorite: v })} placeholder="1" /></div>
                  </div>
                )}
                {formTab === 'calculation' && (
                  <div className={SALAIRES_FORM_GRID_CLASS}>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_amount')}</label><CustomNumberInput required min={0} step={0.01} decimals={2} value={formData.montant} onChange={(v) => setFormData({ ...formData, montant: v })} placeholder="0.00" /></div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_fixed_amount')}</label><CustomSelect value={String(formData.montant_fixe ?? 1)} onChange={(v) => setFormData({ ...formData, montant_fixe: v === '1' ? 1 : 0 })} options={[{ value: '1', label: t('common.yes', { defaultValue: 'نعم' }) }, { value: '0', label: t('common.no', { defaultValue: 'لا' }) }]} autoSelect={false} searchable={false} creatable={false} /></div>
                    {formData.montant_fixe === 0 ? (<><div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_percentage')}</label><CustomNumberInput min={0} max={100} step={0.01} decimals={2} value={formData.pourcentage} onChange={(v) => setFormData({ ...formData, pourcentage: v })} placeholder="0" /></div><div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_calc_base')}</label><CustomSelect value={formData.base_calcul} onChange={(v) => setFormData({ ...formData, base_calcul: v })} options={baseCalculOptions} autoSelect={false} searchable creatable /></div></>) : null}
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_frequency')}</label><CustomSelect value={formData.frequence} onChange={(v) => setFormData({ ...formData, frequence: v })} options={frequenceOptions} autoSelect={false} searchable creatable /></div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_installments')}</label><CustomNumberInput min={0} step={1} value={formData.nombre_echeances} onChange={(v) => setFormData({ ...formData, nombre_echeances: v })} placeholder="0" /></div>
                  </div>
                )}
                {formTab === 'additional' && (
                  <div className={SALAIRES_FORM_GRID_CLASS}>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_start_date')}</label><CustomDatePicker value={formData.date_debut} onChange={(v) => setFormData({ ...formData, date_debut: v })} placeholder={t('common.select')} /></div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_end_date')}</label><CustomDatePicker value={formData.date_fin} onChange={(v) => setFormData({ ...formData, date_fin: v })} placeholder={t('common.select')} /></div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_beneficiary')}</label><input type="text" value={formData.beneficiaire} onChange={(e) => setFormData({ ...formData, beneficiaire: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_organization')}</label><input type="text" value={formData.organisme} onChange={(e) => setFormData({ ...formData, organisme: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_external_ref')}</label><input type="text" value={formData.reference_externe} onChange={(e) => setFormData({ ...formData, reference_externe: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_document')}</label><input type="text" value={formData.document_justificatif} onChange={(e) => setFormData({ ...formData, document_justificatif: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
                    <div className="md:col-span-3 min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_notes')}</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default RetenuesDiversesPage;

